import {Component, EventEmitter, Input, OnChanges, OnInit, Output} from '@angular/core';
import {IonicModule, ModalController} from "@ionic/angular";
import {CreateDialogComponent} from "../../../create-dialog/create-dialog.component";
import {take, takeUntil} from "rxjs";
import {SelectDialogComponent} from "../../../select-dialog/select-dialog.component";
import {GroovePlanApiService} from "../../../../services/grooveplan-api.service";
import {GestionErreursService} from "../../../../services/gestion-erreurs.service";
import {AppUtilsService} from "../../../../services/internals/app-utils.service";
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-dep-field',
  templateUrl: './dep-field.component.html',
  styleUrls: ['./dep-field.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    MatIconModule,
    TranslateModule
  ]
})
export class DepFieldComponent  implements OnInit, OnChanges {
  @Input() depModel: any; // Obligatoire

  @Input() endTitle: any; // Optionnel. Si vide, prend depModel.getDefaultPrefixTitle() + ' ' + depModel.getDefaultTitle();
  @Input() formComponent: any; // Optionnel. Si vide, prend depModel.getDefaultFormComponent() . Composant utilisé pour la modal de création au clic sur le btn "Créer"
  @Input() selectComponent: any; // Optionnel. Si vide, prend depModel.getDefaultSelectComponent() . Composant utilisé pour la modal de sélection au clic sur le btn "Sélectionner"
  @Input() libelleFields: any; // Optionnel. Si vide, prend depModel.getDefaultLibelleFields(). Liste des champs utilisés en tant que libelle en affichage lors de la sélection d'un ou plusieurs événements

  @Input() defaultFormValues: any; // Optionnel. Valeurs par défaut passées à la modal de création
  @Input() fixedFormValues: any; // Optionnel. Valeurs fixes passées à la modal de création
  @Input() listFilters: any; // Optionnel. Filtres appliqués à l'ouverture de modal de sélection
  @Input() listFixedFilters: any; // Optionnel. Filtre non modifiable appliqué à la modal de sélection

  @Input() disableFormBtn: boolean = false;
  @Input() lockFormBtn: boolean = false;
  @Input() disableSelectBtn: boolean = false;
  @Input() lockSelectBtn: boolean = false;

  _value: any;
  @Input() set value(newValue: any) {
    this._value = newValue;
    // Exécuter la méthode ou effectuer les actions appropriées ici
    this.externalValueChangeEvent();
  }

  get value(): any {
    return this._value;
  }

  displayValue: any;
  @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() valueObjChange: EventEmitter<any> = new EventEmitter<any>();

  public index: any;

  onInput(newValue: any) {
    this.valueChangeEvent(newValue)
  }

  public formFields: any

  constructor(
    private modalCtrl: ModalController,
    private groovePlanApiService: GroovePlanApiService,
    private appUtils: AppUtilsService,
    private gestionErreursService: GestionErreursService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit() {
    if(this.depModel) {
      this.index = this.depModel.getIndex();
    } else {
      console.log('Veuillez initialier le model du champ de liaison ' + (this.endTitle ?? ''));
    }

    if(this.formComponent === null || this.formComponent === undefined) {
      this.formComponent = this.depModel.getDefaultFormComponent();
    }
    if(this.selectComponent === null || this.selectComponent === undefined) {
      this.selectComponent = this.depModel.getDefaultSelectComponent();
    }
    if(this.endTitle === null || this.endTitle === undefined) {
      this.endTitle = this.depModel.getDefaultPrefixTitle() + ' ' + this.depModel.getDefaultTitle();
    }
    if(this.libelleFields === null || this.libelleFields === undefined) {
      this.libelleFields = this.depModel.getDefaultLibelleFields();
    }
  }

  ngOnChanges(changes: any) {
    if(changes.value) {
      if(changes.value.currentValue) {
        if(this._value !== changes.value.currentValue) {
          this._value = changes.value.currentValue
          this.externalValueChangeEvent()
        }
      }
    }
  }

  async openCreateModal() {
    if(!this.lockFormBtn) {
      const modal = await this.modalCtrl.create({
        component: CreateDialogComponent,
        componentProps: {
          modalTitle: this.translateService.instant('formGenerator.fields.depField.createModalTitle', { title: this.endTitle }),
          component: this.formComponent,
          defaultValues: this.defaultFormValues,
          fixedValues: this.fixedFormValues,
        }
      });
      modal.present();


      const { data, role } = await modal.onWillDismiss();

      if(role === 'confirm') {
        this.valueChangeEvent(data);
      }
    }
  
  }

  async openSelectModal() {
    if(!this.lockSelectBtn) {
      const modal = await this.modalCtrl.create({
        component: SelectDialogComponent,
        componentProps: {
          modalTitle: this.translateService.instant('formGenerator.fields.depField.selectModalTitle', { title: this.endTitle }),
          component: this.selectComponent,
          filters: this.listFilters,
          fixedFilters: this.listFixedFilters,
          model: this.depModel
        }
      });
      modal.present();


      const { data, role } = await modal.onWillDismiss();

      if(role === 'confirm') {
        this.valueChangeEvent(data);
      }
    }
  }

  deleteSelectedItem() {
    this.valueChangeEvent(null);
  }

  private valueChangeEvent(newValue: any, dispatchEvent = true) {
    let id = null
    if(newValue) {
      id = newValue[this.index];
    }

    this._value = id;
    if(dispatchEvent) {
      this.valueChange.emit(id);
      this.valueObjChange.emit(newValue);
    }

    this.displayValue = '';
    if(newValue) {
      for(let field of this.libelleFields) {
        if(field.indexOf('.') === -1) {
          this.displayValue += newValue[field] + ' ';
        } else {
          this.displayValue += this.appUtils.getDepValue(newValue, field)
        }
      }
    }
  }

  private externalValueChangeEvent() {
    let newValue = [];
    newValue[this.index] = this.value;
    if(this.value && this.depModel) {
      this.groovePlanApiService.entityUrl = this.depModel.getEntityUrl();
      this.groovePlanApiService.getOneById(this.value).then(req => {
        req.pipe(take(1)).subscribe({
          next: res => {
            this.valueChangeEvent(res, false)
          },
          error: (err: any) => {
            this.gestionErreursService.handleError(err)
          }
        })
      })
    } else {
      this.valueChangeEvent(null, false);
    }
  }

}
